import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from 'flowbite-react';
import { FiSettings, FiPlus } from 'react-icons/fi';
import * as wasm from 'sched';
import { useStakeholder } from '../../hooks/useStakeholder';
import { useDatabase } from '../../hooks/useDatabase';
import ValueAllocationCard from './ValueAllocationCard';
import ValueTargetSearch, { type ValueTargetOption } from '../common/ValueTargetSearch';

interface StakeholderDashboardProps {
  stakeholderId: string;
}

/**
 * Operational dashboard for a stakeholder — shows value allocations with status,
 * resource allocation history, and delivery projections.
 * Distinct from the stakeholder detail/configuration page.
 */
const StakeholderDashboard: React.FC<StakeholderDashboardProps> = ({ stakeholderId }) => {
  const { stakeholder, loading, error } = useStakeholder(stakeholderId);
  const { database, applyCommand } = useDatabase();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<ValueTargetOption | null>(null);
  const [newWeight, setNewWeight] = useState(10);

  const addAllocation = async () => {
    if (!stakeholderId || !selectedTarget || newWeight <= 0) return;
    await applyCommand({
      type: 'SetStakeholderValue',
      allocation: {
        stakeholder_id: stakeholderId,
        target: { type: selectedTarget.type, id: selectedTarget.id } as wasm.ValueTarget,
        weight: newWeight,
      },
    });
    setSelectedTarget(null);
    setNewWeight(10);
    setShowAddForm(false);
  };

  const removeAllocation = async (target: { type: string; id: string }) => {
    if (!stakeholderId) return;
    await applyCommand({
      type: 'SetStakeholderValue',
      allocation: {
        stakeholder_id: stakeholderId,
        target: target as wasm.ValueTarget,
        weight: 0,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !stakeholder) {
    return <div className="p-4 text-red-400">{error ?? 'Stakeholder not found'}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with link to config */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-mono font-semibold text-cm-text-primary">{stakeholder.name}</h2>
          {stakeholder.description && (
            <p className="text-sm text-cm-text-muted mt-0.5">{stakeholder.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cm-accent-purple text-cm-bg-deep text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <FiPlus className="w-3.5 h-3.5" />
            Add priority
          </button>
          <Link
            to={`/workspaces/${workspaceId}/stakeholders/${stakeholderId}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cm-border-subtle text-sm text-cm-text-muted hover:text-cm-text-primary hover:border-cm-border-mid transition-colors"
          >
            <FiSettings className="w-3.5 h-3.5" />
            Configure
          </Link>
        </div>
      </div>

      {/* Add allocation form */}
      {showAddForm && (
        <div className="rounded-xl border border-cm-accent-purple/30 bg-cm-bg-surface p-4">
          <h3 className="text-sm font-medium text-cm-text-primary mb-3">Add value allocation</h3>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-cm-text-muted mb-1 block">Target</label>
              <ValueTargetSearch database={database} selected={selectedTarget} onSelect={setSelectedTarget} />
            </div>
            <div className="w-24">
              <label className="text-xs text-cm-text-muted mb-1 block">Weight</label>
              <input
                type="number"
                value={newWeight}
                onChange={(e) => setNewWeight(Number(e.target.value))}
                min={1}
                className="w-full bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg p-2"
              />
            </div>
            <button
              onClick={addAllocation}
              disabled={!selectedTarget}
              className="px-4 py-2 rounded-lg bg-cm-accent-purple text-cm-bg-deep text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-2 text-sm text-cm-text-muted hover:text-cm-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Value allocation cards */}
      {stakeholder.valueAllocations.length === 0 ? (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-8 text-center">
          <p className="text-cm-text-muted mb-2">No value allocations defined.</p>
          <p className="text-sm text-cm-text-muted">
            Add priorities that this stakeholder cares about — tasks, outcomes, or problems.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {stakeholder.valueAllocations.map((alloc, i) => (
            <ValueAllocationCard
              key={`${(alloc.target as any).type}-${(alloc.target as any).id}`}
              allocation={alloc}
              onRemove={() => removeAllocation(alloc.target as any)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StakeholderDashboard;
